import { type KeyboardEvent, type PointerEvent, useRef } from 'react';
import { cx } from '../cx';

/**
 * THE DRAGGABLE LINE BETWEEN TWO PANES (SH-A10, owner-33: "ich kann die sidebar nicht
 * ziehen zum vergroessern oder verkleinern").
 *
 * APPLE'S RULE. "A split view includes dividers between panes that can support dragging to
 * resize them", "Set reasonable defaults for minimum and maximum pane sizes. If people can
 * resize the panes in your app's split view, make sure to use sizes that keep the divider
 * visible" and "Prefer the thin divider style"
 * (https://developer.apple.com/design/human-interface-guidelines/split-views). The
 * developer contract is a minimum, an ideal and a maximum per column
 * (https://developer.apple.com/documentation/swiftui/view/navigationsplitviewcolumnwidth(min:ideal:max:);
 * AppKit's `NSSplitViewItem` carries the same three as thicknesses).
 *
 * A SPLITTER, NOT A BUTTON, and the role is the correct one rather than a way round the
 * measure gate: WAI-ARIA's window-splitter pattern is `role="separator"` with an
 * orientation and a value the arrow keys move, which is what a drag handle over a dividing
 * line is. macOS gives it a narrow grab strip rather than a 40px band that would swallow
 * clicks meant for the panes on either side.
 *
 * WHY IT IS A PRIMITIVE. The list built this once (P4-2) and the shell needed the same
 * thing for the sidebar; a second copy is how two dividers in one window come to move by
 * different steps and announce themselves differently. The list's own copy and the
 * record's inspector adopt this after the shell round merges - both are another parent's
 * files tonight.
 */
export interface SplitDividerProps {
  /** What the screen reader hears: "Resize the sidebar", not "separator". */
  readonly label: string;
  /** The pane's current width in px - the value the splitter announces and moves. */
  readonly width: number;
  readonly min: number;
  readonly max: number;
  /**
   * WHICH SIDE THE PANE IS ON, which is the whole difference between the two dividers in
   * this product: dragging right widens a LEADING pane (the sidebar) and narrows a
   * trailing one (the list's detail pane).
   */
  readonly side?: 'leading' | 'trailing' | undefined;
  readonly onWidth: (next: number) => void;
  readonly className?: string | undefined;
}

/** Arrow moves by a hairline step, Shift-Arrow by four of them. */
const STEP = 16;
const FAST_STEP = 64;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export function SplitDivider({
  label,
  width,
  min,
  max,
  side = 'leading',
  onWidth,
  className,
}: SplitDividerProps) {
  const frame = useRef<number | null>(null);
  const direction = side === 'leading' ? 1 : -1;

  const onPointerDown = (event: PointerEvent<HTMLElement>) => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = width;
    const move = (moveEvent: globalThis.PointerEvent) => {
      const next = clamp(startWidth + (moveEvent.clientX - startX) * direction, min, max);
      /** One write per frame: a pointer fires far more often than the screen paints. */
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      frame.current = requestAnimationFrame(() => onWidth(next));
    };
    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const step = (event.shiftKey ? FAST_STEP : STEP) * (event.key === 'ArrowRight' ? 1 : -1);
    onWidth(clamp(width + step * direction, min, max));
  };

  return (
    // biome-ignore lint/a11y/useSemanticElements: an <hr> is a decorative rule and cannot be a tab stop; WAI-ARIA's window-splitter pattern is a focusable role="separator" carrying a value the arrow keys move
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={Math.round(width)}
      aria-valuemin={Math.round(min)}
      aria-valuemax={Math.round(max)}
      tabIndex={0}
      className={cx('split-divider', className)}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
    />
  );
}
